/* Precios del abono por categoría (A, B, C), mensajes incluidos, paquetes de mensajes por categoría e IVA (AF319).
   Están en parametry/pricing para poder cambiarlos sin deploy. Las functions de Mercado Pago leen lo mismo
   (functions/src/api/libs/pricing.ts) y son las que calculan lo que se cobra: el front solo muestra.
   Los precios se guardan SIN IVA; el IVA se calcula con conIva. */

export const PRICING_DOC = 'pricing';

export interface PricingPackage {
    id: string;          //no se cambia nunca: queda en payments y en purchase_history
    name: string;
    messages: number;
    price: number;       //sin IVA
}

export interface PricingPlan {
    name: string;
    price: number;       //abono por mes, sin IVA
    messages: number;    //mensajes de WhatsApp incluidos por período
    order: number;       //orden en pantalla; mayor = categoría más alta (define subir o bajar)
    visible: boolean;    //false: no se ofrece (por ejemplo la categoría de prueba)
    packages: PricingPackage[];
}

export interface Pricing {
    iva: number;         //alícuota (0.21)
    plans: Record<string, PricingPlan>;
    //aviso de "quedan pocos mensajes": proporción de los mensajes incluidos en la categoría (0.15 = 15%)
    lowMessagesPercent?: number;
}

//si parametry/pricing no tiene lowMessagesPercent (es solo un aviso visual, se puede usar un valor por defecto)
export const DEFAULT_LOW_MESSAGES_PERCENT = 0.15;

//categoría lista para mostrar
export interface PricingCategory extends PricingPlan {
    id: string;              //clave en plans (es lo que se guarda en subscription)
    priceWithIva: number;
    pricePerMessage: number; //abono sin IVA / mensajes incluidos
}

//nombres de los planes anteriores y su categoría equivalente (mismo criterio que las functions).
//Sacar cuando no quede ninguna cuenta con esos nombres (ver docs/tareas/AF319.md)
export const LEGACY_PLANS: Record<string, string> = {
    BASIC: 'A',
    PRO: 'B',
    PROPLUS: 'C',
};

//categoría de un valor de subscription (traduce los nombres viejos). undefined si no tiene suscripción (Free, vacío)
export function planKey(subscription?: string | null): string | undefined {
    if (!subscription || subscription.toLowerCase() === 'free') return undefined;
    return LEGACY_PLANS[subscription] ?? subscription;
}

//categoría visible inmediatamente superior a la de la cuenta (por order). undefined si no tiene categoría o ya está en la más alta
export function nextCategory(pricing: Pricing | undefined | null, subscription?: string | null): PricingCategory | undefined {
    const key = planKey(subscription);
    const categorias = pricingCategories(pricing);
    const actual = key ? pricing?.plans?.[key] : undefined;
    if (!actual) return undefined;
    return categorias.find(c => c.order > actual.order);
}

//umbral de "quedan pocos mensajes" para una categoría: el porcentaje de sus mensajes incluidos (ej. 15% de 150 = 22).
//undefined si no se conoce la categoría
export function lowMessagesThreshold(pricing: Pricing | undefined | null, subscription?: string | null): number | undefined {
    const key = planKey(subscription);
    const messages = Number(key ? pricing?.plans?.[key]?.messages : NaN);
    if (!(messages > 0)) return undefined;
    const percent = Number(pricing?.lowMessagesPercent);
    return Math.floor(messages * (percent >= 0 && percent < 1 ? percent : DEFAULT_LOW_MESSAGES_PERCENT));
}

//precio final con IVA, redondeado a pesos enteros (igual que las functions)
export function conIva(price: number, iva: number): number {
    return Math.round(Number(price) * (1 + Number(iva)));
}

//categorías ordenadas para mostrar. Con incluirOcultas se suman las que tienen visible: false (para probar)
export function pricingCategories(pricing: Pricing | undefined | null, incluirOcultas = false): PricingCategory[] {
    if (!pricing?.plans) return [];
    return Object.entries(pricing.plans)
        .filter(([, plan]) => plan && (plan.visible !== false || incluirOcultas) && Number(plan.price) > 0)
        .map(([id, plan]) => ({
            ...plan,
            id,
            packages: plan.packages ?? [],
            priceWithIva: conIva(plan.price, pricing.iva),
            pricePerMessage: Number(plan.messages) > 0 ? Math.round(plan.price / plan.messages * 100) / 100 : 0,
        }))
        .sort((a, b) => a.order - b.order);
}
